import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import CloudsImg from '../../../static/images/workday/clouds.svg';
import MagnifyingLens from '../../../static/images/workday/magnifyingLens.svg';
import { Typography } from '../../Typography/Typography';
import { Button } from '../../Button/Button';
import { CopyToClipboard } from '../../CopyToClipboard/CopyToClipboard';
import { MouseParallaxIcon } from '../../Icon/MouseParallaxIcon';
import { useIntlUds } from '../../../translations/UdsIntlProvider';
import { useDataTableContext } from '../utils/DataTableContext';
import { usePropsForwarderContext } from '../plugins/impl/context/ForwarderContext';

export interface DataTableErrorProps {
  error: string | ReactNode | undefined;
  useHistory: () => {
    push: (arg0: string) => void;
    go: (arg0: number) => void;
  };
  useLocation: () => {
    pathname: string;
  };
}

const forcedEmptyQuery = `?query=%7B%7D`;

export const DataTableError: React.FC<React.PropsWithChildren<DataTableErrorProps>> = (props) => {
  const { setForwarderProps } = usePropsForwarderContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    setForwarderProps('error', props);
  }, [props]);

  return null;
};

export const DataTableErrorInternalComponent = () => {
  const { intl } = useIntlUds();
  const { previousURLKey } = useDataTableContext();
  const { getProps } = usePropsForwarderContext();

  const { error, useHistory, useLocation } = getProps('error') ?? {};
  const history = useHistory!();
  const location = useLocation!();

  const reloadPage = useCallback(() => {
    if (previousURLKey) {
      window.localStorage.removeItem(previousURLKey);
    }
    history?.push(`${location?.pathname}${forcedEmptyQuery}`);
    history?.go(0);
  }, [history, location?.pathname, previousURLKey]);

  const [showError, setShowError] = useState(false);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  if (typeof error !== 'string') return <>{error}</>;
  return (
    <StyledErrorPosition>
      <StyledErrorContainer data-test="data-table-error">
        <MouseParallaxIcon height={150} width={300} backgroundImgUrl={CloudsImg} foregroundImgUrl={MagnifyingLens} />
        <Typography>{intl.DataTable.Error.message}</Typography>
        <Button
          onClick={reloadPage}
          dataTest="clear_query__action__button"
          variant="contained"
          color="primary"
          key="clear-query"
        >
          {intl.DataTable.Error.reloadBtn}
        </Button>
        <Button
          dataTest="show_error__action__button"
          color="primary"
          key="show-error"
          size="small"
          onClick={() => setShowError((prevState) => !prevState)}
        >
          {showError ? intl.DataTable.Error.hideDetailsButton : intl.DataTable.Error.seeDetailsButton}
        </Button>
        {showError && (
          <CopyToClipboard
            wrapText
            valueToCopy={error}
            tooltipText={intl.DataTable.Error.copyErrorTooltipText.beforeCopy}
          >
            {error}
          </CopyToClipboard>
        )}
      </StyledErrorContainer>
    </StyledErrorPosition>
  );
};

const StyledErrorPosition = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledErrorContainer = styled.div`
  flex: 1 1 auto;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
  > div {
    margin-bottom: ${({ theme }) => theme.spacingSize.l};
  }
`;
